// Load/init Sign in with Apple JS and parse its response into a prefill profile

import { decodeJwt } from './decodeJwt'

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

let scriptPromise = null

const loadAppleScript = () => {
  if (typeof window !== 'undefined' && window.AppleID) {
    return Promise.resolve()
  }
  if (scriptPromise) {
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = APPLE_SCRIPT_SRC
    script.async = true
    script.onload = resolve
    script.onerror = (error) => {
      scriptPromise = null
      reject(error)
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * appleSignIn: loads/inits `appleid.auth.js` and runs the popup sign-in flow, returning
 * the id token, nonce and prefill profile
 *
 * @param {string} nonce Raw nonce used for the sign-in request
 * @return {Promise<{idToken: string, nonce: string, profile: {nameFirst: string, nameLast: string, email: string}}>}
 */
export const appleSignIn = async (nonce) => {
  await loadAppleScript()
  window.AppleID.auth.init({
    clientId: process.env.NEXT_PUBLIC_APPLE_SERVICES_ID,
    redirectURI: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI,
    scope: 'name email',
    usePopup: true,
    nonce,
  })

  const response = await window.AppleID.auth.signIn()
  const idToken = response?.authorization?.id_token
  const claims = decodeJwt(idToken) || {}
  const name = response?.user?.name

  return {
    idToken,
    nonce,
    profile: {
      nameFirst: name?.firstName || '',
      nameLast: name?.lastName || '',
      // the id_token claim comes FIRST: starbase re-verifies the token and derives the
      // identity email from that claim alone, then rejects signup unless it matches the
      // account email we submit. `response.user.email` (sent only on the first
      // authorization) is the fallback for when the claim is absent — trusting it over
      // the claim would let the two sides disagree and fail signup.
      email: claims.email || response?.user?.email || '',
    },
  }
}
